import { Flex } from "antd";
import { useCallback, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../hooks/redux-hooks";
import { useAudioStatusCheck } from "../../hooks/useAudioStatusCheck";
import { getClientDetailsByUUID } from "../../service/client.service";
import { getNotesListByClientUUID } from "../../service/notes.service";
import { setLoadingNotesState, setSelectedClient } from "../../slices/appStateSlice";
import { setTotalNotes } from "../../slices/notesSlice";
import { eventEmitter } from "../../utils/eventEmitter";
import NotesSection from "./NotesSection.component";
import styles from "./home.module.scss";

const ClientNotes = () => {
  const componentName = "home";
  const dispatch = useAppDispatch();
  const { clientUuid } = useParams<{ clientUuid: string }>();
  const abortControllerRef = useRef<AbortController | null>(null);
  const { clients, sharedClients } = useAppSelector((state) => state.client);
  const { isNotesViewVisible } = useAppSelector((state) => state.appState);
  const { notes, updateNotes: setNotes } = useAudioStatusCheck([]);

  const fetchNotes = useCallback(
    async (pageNumber = 1) => {
      if (!clientUuid) return;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();
      try {
        dispatch(setLoadingNotesState(true));
        const response = await getNotesListByClientUUID(clientUuid, pageNumber, abortControllerRef.current.signal);
        const data = await response.data;
        setNotes(data?.results);
        dispatch(setTotalNotes(data?.count));
        dispatch(setLoadingNotesState(false));
      } catch (error) {
        console.error("Error fetching client notes:", error);
      }
    },
    [clientUuid, dispatch, setNotes],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: Only re-fetch when clientUuid changes, not when client list updates
  useEffect(() => {
    if (clientUuid) {
      // First, set from cached list data for immediate display (check both owned and shared)
      const cachedClient =
        clients.find((c) => c.uuid === clientUuid) || sharedClients.find((c) => c.uuid === clientUuid);
      if (cachedClient) {
        dispatch(setSelectedClient(cachedClient));
      }

      // Then fetch full client details (includes progress_summary, context, etc.)
      getClientDetailsByUUID(clientUuid)
        .then((response) => {
          dispatch(setSelectedClient(response.data));
        })
        .catch((error) => {
          console.error("Error fetching client details:", error);
        });
    }
  }, [clientUuid, dispatch]);

  // Fetch notes when needed
  useEffect(() => {
    if (!isNotesViewVisible) {
      fetchNotes();
    }
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchNotes, isNotesViewVisible]);

  const refreshHandler = useCallback(() => {
    fetchNotes();
  }, [fetchNotes]);

  useEffect(() => {
    eventEmitter.on("refreshNotes", refreshHandler);

    return () => {
      eventEmitter.off("refreshNotes", refreshHandler);
    };
  }, [refreshHandler]);

  return (
    <Flex className={!isNotesViewVisible ? `${styles[`${componentName}__clientnotes`]}` : ""} vertical>
      <NotesSection notes={notes} setNotes={setNotes} fetchNotes={fetchNotes} />
    </Flex>
  );
};

export default ClientNotes;
